<script lang="ts">
	/**
	 * The credential form for one email sender, generated from the adapter's own
	 * `describeCredentials()` — the same construction as `ProviderCredentialsForm`, and for the
	 * same reason: adding an SMTP relay, Resend, SES or Postmark adapter must not mean adding UI.
	 *
	 * The two rules that matter are carried over verbatim (Design §12):
	 *
	 * - **Secrets are write-only.** A read returns a mask, never a value, so every secret field
	 *   starts empty with its mask as the placeholder.
	 * - **Every field is submitted on every save.** An untouched field submits the sentinel
	 *   `"unchanged"`, so the server keeps the stored ciphertext byte for byte. Sending an empty
	 *   string instead — the obvious implementation — blanks a live password on an unrelated
	 *   change to the from-name, and the store then stops sending receipts with nothing pointing
	 *   at the edit that caused it.
	 *
	 * On top of the credentials sit the fields that are the sender's *identity* rather than its
	 * access: from-address, from-name and reply-to. They are stored in the clear, so unlike the
	 * credentials they are shown as values.
	 */

	import SelectField from './SelectField.svelte';
	import { Badge, Button, Input, Toggle } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import type {
		CredentialFieldSpec,
		EmailProviderConfig,
		EmailProviderType,
		EmailProviderWriteInput,
		EmailProviderTypeDescriptor
	} from '../AppTypes';
	import { UNCHANGED_SECRET } from '../AppTypes';
	import ConfirmButton from './ConfirmButton.svelte';
	import FormField from './FormField.svelte';
	import Panel from './Panel.svelte';
	import RelativeDate from './RelativeDate.svelte';

	/** The row being edited, or null to render the create form. */
	export let provider: EmailProviderConfig | null = null;
	/** Adapter types from `GET /api/admin/email-provider/types`. */
	export let types: EmailProviderTypeDescriptor[] = [];
	export let saving: boolean = false;
	export let verifying: boolean = false;
	export let promoting: boolean = false;
	export let testing: boolean = false;

	const dispatch = createEventDispatcher<{
		save: { input: EmailProviderWriteInput };
		verify: Record<string, never>;
		makeDefault: Record<string, never>;
		sendTest: { to: string };
		cancel: Record<string, never>;
	}>();

	let selectedType: EmailProviderType | '' = '';
	let name: string = '';
	let fromAddress: string = '';
	let fromName: string = '';
	let replyToAddress: string = '';
	let isActive: boolean = true;
	/** Field name → what the operator typed. Empty plus untouched means "keep what is stored". */
	let credentials: Record<string, string> = {};
	/** Field name → whether it was edited, so an emptied optional field can actually be cleared. */
	let touched: Record<string, boolean> = {};
	/** Where the test message goes. Empty means "the address the server picks". */
	let testRecipient: string = '';

	let loadedFrom: EmailProviderConfig | null | undefined = undefined;

	$: if (provider !== loadedFrom) {
		loadedFrom = provider;
		selectedType = provider?.type ?? types[0]?.type ?? '';
		name = provider?.name ?? '';
		fromAddress = provider?.fromAddress ?? '';
		fromName = provider?.fromName ?? '';
		replyToAddress = provider?.replyToAddress ?? '';
		isActive = provider?.isActive ?? true;
		credentials = {};
		touched = {};
	}

	$: isCreate = provider === null;
	$: descriptor = types.find(candidate => candidate.type === selectedType) ?? null;
	$: fields = descriptor?.credentialFields ?? [];

	/**
	 * A row whose adapter this build no longer registers. Its fields cannot be rendered, so the
	 * form says so instead of showing an empty credential section that reads as "nothing to set".
	 */
	$: isUnknownType = provider !== null && descriptor === null;

	function maskOf(field: CredentialFieldSpec): string {
		return provider?.credentialMasks?.[field.name] ?? '';
	}

	function placeholderOf(field: CredentialFieldSpec): string {
		if (isCreate) {
			return field.placeholder ?? '';
		}
		const mask = maskOf(field);
		return mask.length > 0 ? mask : 'Not set';
	}

	/** What this field contributes to the payload. */
	function submissionValue(field: CredentialFieldSpec): string {
		const typed = credentials[field.name] ?? '';
		if (isCreate) {
			return typed;
		}
		// Untouched means untouched — not empty. The server preserves the stored ciphertext.
		return touched[field.name] ? typed : UNCHANGED_SECRET;
	}

	function fieldError(field: CredentialFieldSpec): string {
		if (!field.isRequired) {
			return '';
		}
		if (isCreate && (credentials[field.name] ?? '').length === 0) {
			return 'Required.';
		}
		if (!isCreate && touched[field.name] && (credentials[field.name] ?? '').length === 0) {
			return 'Required. Clear the field only if you are replacing it.';
		}
		return '';
	}

	/** Deliberately loose: the server holds the real rule, this only stops an obvious typo. */
	function looksLikeAddress(value: string): boolean {
		return /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(value.trim());
	}

	$: fromAddressError =
		fromAddress.trim().length > 0 && !looksLikeAddress(fromAddress)
			? 'That does not look like an email address.'
			: '';
	$: replyToError =
		replyToAddress.trim().length > 0 && !looksLikeAddress(replyToAddress)
			? 'That does not look like an email address.'
			: '';
	$: credentialErrors = fields.some(field => fieldError(field).length > 0);
	$: canSave =
		!saving &&
		name.trim().length > 0 &&
		selectedType.length > 0 &&
		looksLikeAddress(fromAddress) &&
		replyToError.length === 0 &&
		!credentialErrors;

	type BadgeTone = 'green' | 'red' | 'yellow';

	const HEALTH_TONE: Record<string, BadgeTone> = {
		healthy: 'green',
		unhealthy: 'red',
		undecryptable: 'red',
		unknown: 'yellow'
	};

	/** Status is carried by the text, never by the colour alone. */
	const HEALTH_LABEL: Record<string, string> = {
		healthy: 'Credentials verified',
		unhealthy: 'Verification failed',
		undecryptable: 'Credentials cannot be decrypted',
		unknown: 'Not verified yet'
	};

	function fnSave(): void {
		if (!canSave) {
			return;
		}

		const payload: Record<string, string> = {};
		for (const field of fields) {
			payload[field.name] = submissionValue(field);
		}

		const input: EmailProviderWriteInput = {
			name: name.trim(),
			credentials: payload,
			fromAddress: fromAddress.trim(),
			fromName: fromName.trim().length > 0 ? fromName.trim() : null,
			replyToAddress: replyToAddress.trim().length > 0 ? replyToAddress.trim() : null,
			isActive
		};
		if (isCreate && selectedType !== '') {
			input.type = selectedType;
		}

		dispatch('save', { input });
	}

	function fnSendTest(): void {
		dispatch('sendTest', { to: testRecipient.trim() });
	}
</script>

<Panel faIcon="paper-plane" title={isCreate ? 'New email sender' : provider?.name}>
	<svelte:fragment slot="titleRight">
		{#if provider}
			{#if provider.isDefault}
				<Badge color="green">Default sender</Badge>
			{/if}
			{#if !provider.isActive}
				<Badge color="dark">Inactive</Badge>
			{/if}
			<Badge color={HEALTH_TONE[provider.healthStatus] ?? 'yellow'}>
				{HEALTH_LABEL[provider.healthStatus] ?? provider.healthStatus}
			</Badge>
		{/if}
	</svelte:fragment>

	<form class="space-y-4" on:submit|preventDefault={fnSave}>
		<div class="grid md:grid-cols-2 gap-4">
			<FormField
				id="email-provider-type"
				label="Adapter"
				required
				helpText="Which service actually hands the message over. It cannot be changed later. Create a second sender and promote it instead."
			>
				{#if isCreate}
					<SelectField
						id="email-provider-type"
						bind:value={selectedType}
						disabled={saving}
						items={types.map(type => ({ value: type.type, name: type.displayName }))}
						placeholder="Choose an adapter"
					/>
				{:else}
					<Input
						id="email-provider-type"
						value={descriptor?.displayName ?? provider?.type ?? ''}
						disabled
					/>
				{/if}
			</FormField>

			<FormField
				id="email-provider-name"
				label="Name"
				required
				description="Admin-facing label, e.g. “Postmark (transactional)”."
			>
				<Input
					id="email-provider-name"
					bind:value={name}
					disabled={saving}
					placeholder="Postmark (transactional)"
				/>
			</FormField>

			<FormField
				id="email-provider-from-address"
				label="From address"
				required
				error={fromAddressError}
				description="The address customers see, and the one that has to pass SPF and DKIM for this sender."
			>
				<Input
					id="email-provider-from-address"
					type="email"
					autocomplete="off"
					bind:value={fromAddress}
					disabled={saving}
					placeholder="receipts@example.com"
				/>
			</FormField>

			<FormField
				id="email-provider-from-name"
				label="From name"
				description="Shown beside the address in a mail client. Leave blank to send the address alone."
			>
				<Input
					id="email-provider-from-name"
					bind:value={fromName}
					disabled={saving}
					placeholder="Example Store"
				/>
			</FormField>

			<FormField
				id="email-provider-reply-to"
				label="Reply-to address"
				error={replyToError}
				description="Where a customer's reply lands, and the support address the storefront shows when this is the default provider. Leave blank and replies go to the from address, which should be a mailbox somebody reads."
			>
				<Input
					id="email-provider-reply-to"
					type="email"
					autocomplete="off"
					bind:value={replyToAddress}
					disabled={saving}
					placeholder="support@example.com"
				/>
			</FormField>

			<FormField
				id="email-provider-active"
				label="Active"
				description="Inactive stops this sender being used. Messages queue rather than being dropped."
			>
				<Toggle id="email-provider-active" bind:checked={isActive} disabled={saving}>
					{isActive ? 'Available to send' : 'Not sending'}
				</Toggle>
			</FormField>
		</div>

		{#if isUnknownType}
			<div class="widget bg-depth text-sm">
				<p class="card-label mb-1">
					<i class="fa-solid fa-triangle-exclamation text-[11px] mr-1" />Unknown adapter
				</p>
				<p class="text-default">
					No adapter is registered for type <code>{provider?.type}</code> in this build, so its
					credential fields cannot be shown. The stored credentials are untouched; deploying a
					build that registers this adapter will restore the form.
				</p>
			</div>
		{:else}
			<div class="space-y-4">
				<p class="card-label">
					<i class="fa-solid fa-key text-[11px] mr-1" />Credentials
				</p>

				{#each fields as field (field.name)}
					{@const controlID = `email-credential-${field.name}`}
					<FormField
						id={controlID}
						label={field.label}
						required={field.isRequired}
						helpText={field.help ?? ''}
						description={isCreate
							? ''
							: 'Leave blank to keep the stored value. Type a new one to replace it.'}
						error={fieldError(field)}
					>
						<Input
							id={controlID}
							type={field.isSecret ? 'password' : 'text'}
							autocomplete="off"
							placeholder={placeholderOf(field)}
							disabled={saving}
							bind:value={credentials[field.name]}
							on:input={() => (touched[field.name] = true)}
						/>
					</FormField>
				{/each}

				{#if fields.length === 0}
					<p class="text-sm text-default">This adapter declares no credential fields.</p>
				{/if}
			</div>
		{/if}

		<div class="flex flex-wrap items-center gap-3">
			<Button type="submit" color="primary" disabled={!canSave}>
				{#if saving}
					<i class="fa-solid fa-spinner fa-spin mr-2" />
				{/if}
				{isCreate ? 'Create sender' : 'Save changes'}
			</Button>

			<Button color="alternative" disabled={saving} on:click={() => dispatch('cancel', {})}>
				Cancel
			</Button>
		</div>
	</form>

	{#if provider}
		<hr class="my-5 border-gray-200 dark:border-gray-700" />

		<div class="grid lg:grid-cols-2 gap-4">
			<div class="widget bg-depth">
				<p class="card-label mb-2">
					<i class="fa-solid fa-stethoscope text-[11px] mr-1" />Sender health
				</p>

				<div class="flex flex-wrap items-center gap-2 mb-2">
					<!-- In words as well as colour: a red dot alone tells a colour-blind operator, or
					     anybody skimming, nothing about what is actually wrong. -->
					<Badge color={HEALTH_TONE[provider.healthStatus] ?? 'yellow'}>
						{HEALTH_LABEL[provider.healthStatus] ?? provider.healthStatus}
					</Badge>
					{#if provider.healthCheckedAt}
						<span class="text-xs text-default">
							Checked <RelativeDate date={provider.healthCheckedAt} size="xs" />
						</span>
					{/if}
				</div>

				{#if provider.healthError}
					<p class="text-xs text-red-600 dark:text-red-500 mb-2 break-words">
						{provider.healthError}
					</p>
				{/if}

				<p class="text-xs text-default mb-3">
					Verifying opens a connection with these credentials. It proves the sender can be reached,
					not that a message from this address will be accepted.
				</p>

				<Button
					size="sm"
					color="alternative"
					disabled={verifying}
					on:click={() => dispatch('verify', {})}
				>
					{#if verifying}
						<i class="fa-solid fa-spinner fa-spin mr-2" />
					{:else}
						<i class="fa-solid fa-circle-check mr-2" />
					{/if}
					Verify credentials
				</Button>
			</div>

			<div class="widget bg-depth">
				<p class="card-label mb-2">
					<i class="fa-solid fa-envelope-circle-check text-[11px] mr-1" />Send a test message
				</p>

				<p class="text-xs text-default mb-3">
					Sends one real message now. A from-address that fails DMARC, a blocked port or a rotated
					password is invisible from a saved row, and otherwise surfaces at a customer's receipt.
				</p>

				<div class="flex items-center gap-2">
					<Input
						id="email-provider-test-recipient"
						type="email"
						autocomplete="off"
						size="sm"
						bind:value={testRecipient}
						disabled={testing}
						placeholder="Your own address (optional)"
					/>
					<Button size="sm" color="alternative" disabled={testing} on:click={fnSendTest}>
						{#if testing}
							<i class="fa-solid fa-spinner fa-spin mr-2" />
						{:else}
							<i class="fa-solid fa-paper-plane mr-2" />
						{/if}
						Send test
					</Button>
				</div>
			</div>
		</div>

		{#if !provider.isDefault}
			<div class="mt-4">
				<ConfirmButton
					size="sm"
					color="primary"
					disabled={promoting || !provider.isActive}
					confirmText={`Make “${provider.name}” the default sender? Every message the store queues will be sent through it.`}
					on:confirm={() => dispatch('makeDefault', {})}
				>
					{#if promoting}
						<i class="fa-solid fa-spinner fa-spin mr-2" />
					{:else}
						<i class="fa-solid fa-star mr-2" />
					{/if}
					Make default sender
				</ConfirmButton>

				{#if !provider.isActive}
					<p class="text-xs text-default mt-1">Activate the sender before making it the default.</p>
				{/if}
			</div>
		{/if}
	{/if}
</Panel>
